import React from "react";
import { CardHeaderNoIcon } from "components/Cards/CardHeaderWithIcon";
import { CardMaps } from "components/Cards/CardMaps";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import { getMonthName } from "utils/Time";
import { Table } from "reactstrap";
import {
    getIconByPlantSecond_readingStatus,
    getIconByPlantStatus,
    isSolarPhotovoltaicByCaller,
    isSolarThermalByCaller,
    isThermalByCaller,
    isWaterByCaller
} from "utils/Types";
import {
    BsThermometerHalf,
    BsThermometerSnow,
    BsThermometerSun,
} from "react-icons/bs";
import {
    RiContrastDrop2Fill,
    RiContrastDrop2Line
} from "react-icons/ri";
import config from "/app/src/config.js";

const isMobileOnly = config.mbl_version;

const isAlert = (value) => {
    return value !== undefined && value !== null && value !== 0 && value !== 1;
};

const hasSecondReadingAlert = (value) => {
    return value === 1;
};

const PlantLocationInfo = ({ ...props }) => {
    return (
        <>
            <div>{props.data.plant_name ?? <Skeleton />}</div>
            <div>{props.data.plant_address ?? <Skeleton />}</div>
            <div>
                {props.data.plant_town_name
                    ? `${props.data.plant_town_name} (${props.data.plant_province_name})`
                    : <Skeleton />}
            </div>

            {props.data.coordinates && (
                <div style={{ marginTop: "15px", marginBottom: "15px" }}>
                    <CardMaps {...props} coordinates={props.data.coordinates} />
                </div>
            )}
        </>
    );
};

export function SolarThermalPlantDesc({ ...props }) {
    const { t } = useTranslation();

    return (
        <>
            <PlantLocationInfo {...props} />


            <div style={{ marginBottom: "5px", color: "#323232" }}>
                {t("plant.collector_field")}:
            </div>

            <div>
                {props.data.plant_collector_area
                    ? `${props.data.plant_collector_area}m²`
                    : <Skeleton />}
            </div>
        </>
    );
}

export function SolarPhotovoltaicPlantDesc({ ...props }) {
    const { t } = useTranslation();

    return (
        <>
            <PlantLocationInfo {...props} />

            <div style={{ marginBottom: "5px", color: "#323232" }}>
                {t("plant.aux_energy")}:
            </div>

            <div>{t("aux_energy.electricity")}</div>

            <div style={{ fontSize: "13px" }}>
                {props.data.plant_aux_energy_price
                    ? `${t("price")}: ${props.data.plant_aux_energy_price * 1000}€/kWh.`
                    : <Skeleton />}
            </div>

            <div style={{ marginBottom: "5px", fontSize: "13px" }}>
                {props.data.plant_aux_energy_emissions
                    ? `${t("emissions")}: ${props.data.plant_aux_energy_emissions}kgC0₂/kWh.`
                    : <Skeleton />}
            </div>

            <hr />

            <div style={{ marginBottom: "5px", color: "#323232" }}>
                {t("plant.collector_field")}:
            </div>

            <div>
                {props.data.plant_collector_area
                    ? `${t("area")}: ${props.data.plant_collector_area}m²`
                    : <Skeleton />}
            </div>

            <div>
                {props.data.plant_power
                    ? `${t("power")}: ${props.data.plant_power}kWp`
                    : <Skeleton />}
            </div>
        </>
    );
}

export function ThermalPlantDesc({ ...props }) {
    return (
        <>
            <PlantLocationInfo {...props} />
        </>
    );
}

export function WaterPlantDesc({ ...props }) {
    const { t } = useTranslation();

    return (
        <>
            <PlantLocationInfo {...props} />

            <div style={{ marginBottom: "5px", color: "#323232" }}>
                {t("plant.consumption")}:
            </div>

            <div>
                {props.data.plant_max_production
                    ? `${t("plant.consumption_max")}: ${props.data.plant_max_production}L/h`
                    : <Skeleton />}
            </div>

            <div>
                {props.data.plant_provider_price
                    ? `${t("price")}: ${props.data.plant_provider_price}€/m3`
                    : <Skeleton />}
            </div>
        </>
    );
}

export function PlantAlertsSummary({ ...props }) {
    const { t } = useTranslation();

    const alerts = [];

    const isPlantOk = props.data.plant_status_id === 1 || props.data.plant_status_name === "OK";

    if (isPlantOk) {
        return (
            <div className={`card ${isMobileOnly && "card-mbl"}`}>
                <CardHeaderNoIcon title={t("alerts")} />

                <div className={`card-body ${isMobileOnly && "card-body-mbl pt-20-mbl"}`}>
                    <div
                        className="d-flex align-items-center"
                        style={{
                            padding: "18px 20px",
                            fontSize: "14px",
                            color: "#6e6b7b",
                        }}
                    >
                        Sin alertas
                    </div>
                </div>
            </div>
        );
    }


    if (isAlert(props.data.plant_hourly_alert)) {
        alerts.push({
            key: "hourly",
            icon: getIconByPlantStatus(props, props.data.plant_hourly_alert),
            label: "Horaria"
        });
    }

    if (isAlert(props.data.plant_daily_alert)) {
        alerts.push({
            key: "daily",
            icon: getIconByPlantStatus(props, props.data.plant_daily_alert),
            label: "Diaria"
        });
    }

    if (hasSecondReadingAlert(props.data.second_reading)) {
        alerts.push({
            key: "second_reading",
            icon: getIconByPlantSecond_readingStatus(props, props.data.second_reading),
            label: "Alerta secundaria"
        });
    }

    if (alerts.length === 0) {
        alerts.push({
            key: "no_alerts",
            icon: null,
            label: "Sin alertas"
        });
    }

    return (
        <div className={`card ${isMobileOnly && "card-mbl"}`}>
            <CardHeaderNoIcon title={t("alerts")} />

            <div className={`card-body ${isMobileOnly && "card-body-mbl pt-20-mbl"}`}>
                {alerts.map((alert, index) => (
                    <div
                        key={alert.key}
                        className="d-flex align-items-center"
                        style={{
                            padding: "18px 20px",
                            borderTop: index === 0 ? "0" : "1px solid #ebe9f1",
                        }}
                    >
                        {alert.icon && (
                            <div style={{ marginRight: "10px" }}>
                                {alert.icon}
                            </div>
                        )}

                        <div
                            style={{
                                fontSize: "14px",
                                color: "#6e6b7b",
                            }}
                        >
                            {alert.label}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export function ProjectedDataMonthly({ ...props }) {
    const { t } = useTranslation();

    return (
        <>
            <Table size="sm" responsive>
                <thead>
                    <tr>
                        <th>{t("month")}</th>
                        <th>
                            {isWaterByCaller(props)
                                ? t("units.liters")
                                : (
                                    <span style={{ textTransform: "lowercase" }}>
                                        k<span style={{ textTransform: "uppercase" }}>W</span>h
                                    </span>
                                )}
                        </th>
                    </tr>
                </thead>

                <tbody>
                    <tr><td>{getMonthName("01")}</td><td>{props.data.plant_production_jan ?? <Skeleton />}</td></tr>
                    <tr><td>{getMonthName("02")}</td><td>{props.data.plant_production_feb ?? <Skeleton />}</td></tr>
                    <tr><td>{getMonthName("03")}</td><td>{props.data.plant_production_mar ?? <Skeleton />}</td></tr>
                    <tr><td>{getMonthName("04")}</td><td>{props.data.plant_production_apr ?? <Skeleton />}</td></tr>
                    <tr><td>{getMonthName("05")}</td><td>{props.data.plant_production_may ?? <Skeleton />}</td></tr>
                    <tr><td>{getMonthName("06")}</td><td>{props.data.plant_production_jun ?? <Skeleton />}</td></tr>
                    <tr><td>{getMonthName("07")}</td><td>{props.data.plant_production_jul ?? <Skeleton />}</td></tr>
                    <tr><td>{getMonthName("08")}</td><td>{props.data.plant_production_aug ?? <Skeleton />}</td></tr>
                    <tr><td>{getMonthName("09")}</td><td>{props.data.plant_production_sep ?? <Skeleton />}</td></tr>
                    <tr><td>{getMonthName("10")}</td><td>{props.data.plant_production_oct ?? <Skeleton />}</td></tr>
                    <tr><td>{getMonthName("11")}</td><td>{props.data.plant_production_nov ?? <Skeleton />}</td></tr>
                    <tr><td>{getMonthName("12")}</td><td>{props.data.plant_production_dec ?? <Skeleton />}</td></tr>
                </tbody>
            </Table>
        </>
    );
}

const MeasurementRow = (units, data, title, icon, iconClass = "bg-light-primary") => {
    return (
        <>
            <div className="transaction-item mb-1">
                <div className="d-flex">
                    <div className={`avatar rounded ${iconClass} me-1`} style={{ padding: "5px" }}>
                        <span className="avatar-content">{icon}</span>
                    </div>

                    <div>
                        <h6 className="transaction-title" style={{ marginBottom: 0 }}>
                            {Number.isInteger(data) ? `${data}${units}` : data}
                        </h6>
                        {title}
                    </div>
                </div>
            </div>
        </>
    );
};

export function WaterCurrentMeasurementData({ ...props }) {
    const { t } = useTranslation();

    let iconClass;
    let tMax = !props.data.plant_max_production ? <Skeleton /> : props.data.plant_max_production;
    let tCur = !props.data.plant_current_value ? "??" : props.data.plant_current_value;

    if (Number.isInteger(tMax) && Number.isInteger(tCur)) {
        if (tMax < tCur) {
            iconClass = "bg-light-danger";
        } else if (tMax === tCur) {
            iconClass = "bg-light-warning";
        } else if (tMax > tCur) {
            iconClass = "bg-light-success";
        }
    }

    return (
        <>
            {MeasurementRow("L/h", tMax, `${t("limit")}`, <RiContrastDrop2Fill size="23px" />)}
            {MeasurementRow("L/h", tCur, t("last_reading"), <RiContrastDrop2Line size="25px" />, iconClass)}
        </>
    );
}

export function ThermalCurrentMeasurementData({ ...props }) {
    const { t } = useTranslation();

    let iconClass;
    let tMax = !props.data.plant_margin_production_over ? <Skeleton /> : props.data.plant_margin_production_over;
    let tMin = !props.data.plant_margin_production_under ? <Skeleton /> : props.data.plant_margin_production_under;
    let tCur = !props.data.plant_current_value ? "??" : props.data.plant_current_value;

    if (Number.isInteger(tMax) && Number.isInteger(tMin) && Number.isInteger(tCur)) {
        if (tMax < tCur || tCur < tMin) {
            iconClass = "bg-light-danger";
        } else if (tMax === tCur || tCur === tMin) {
            iconClass = "bg-light-warning";
        } else if (tMax > tCur || tCur < tMin) {
            iconClass = "bg-light-success";
        }
    }

    return (
        <>
            {MeasurementRow("ºC", tMax, `${t("maximum_female")}`, <BsThermometerSun size="25px" style={{ marginLeft: "5px" }} />)}
            {MeasurementRow("ºC", tCur, t("last_reading"), <BsThermometerHalf size="25px" />, iconClass)}
            {MeasurementRow("ºC", tMin, `${t("minimum_female")}`, <BsThermometerSnow size="25px" style={{ marginLeft: "5px" }} />)}
        </>
    );
}


export function PlantSummary({ ...props }) {
    const { t } = useTranslation();

    let title;
    let information;
    let subtitle = "";

    /*
     * ============================================================
     * DESCRIPCIÓN
     * ============================================================
     *
     * La descripción solamente se prepara cuando type === 2.
     *
     * Actualmente está desactivada desde PagePlant.js,
     * donde hemos comentado el componente correspondiente.
     *
     * Conservamos toda su funcionalidad para recuperarla
     * cuando sea necesario.
     */

    if (props.type === 2) {

        title = t("description");

        if (isSolarThermalByCaller(props)) {
            information = <SolarThermalPlantDesc {...props} />;
        } else if (isSolarPhotovoltaicByCaller(props)) {
            information = <SolarPhotovoltaicPlantDesc {...props} />;
        } else if (isThermalByCaller(props)) {
            information = <ThermalPlantDesc {...props} />;
        } else if (isWaterByCaller(props)) {
            information = <WaterPlantDesc {...props} />;
        }

    }

    /*
     * ============================================================
     * RENDER
     * ============================================================
     */

    return (
        <>

            {/*
             * ALERTAS
             * Solo se muestran cuando type === 1.
             */}

            {props.type === 1 && (
                <PlantAlertsSummary {...props} />
            )}


            {/*
             * DESCRIPCIÓN
             * Solo se muestra cuando type === 2.
             *
             * Actualmente PagePlant.js no utiliza este tipo
             * porque lo hemos dejado comentado.
             */}

            {props.type === 2 && information && (

                <div
                    className={`card ${
                        isMobileOnly ? "card-mbl" : ""
                    }`}
                >

                    <CardHeaderNoIcon
                        title={title}
                        subtitle={subtitle}
                    />

                    <div
                        className={`card-body ${
                            isMobileOnly
                                ? "card-body-mbl pt-20-mbl"
                                : ""
                        }`}
                    >
                        {information}
                    </div>

                </div>

            )}

        </>
    );
}
